import type { FC, PropsWithChildren } from 'react'
import { Box } from '@mui/material'
import { Body1, H6 } from '@dhl-ui/components'
import type { MatchWithHighlights } from '~/search/model/search.js'

type Props = {
  disableTypography?: boolean
  emphasis?: boolean
  matches?: readonly MatchWithHighlights[]
  fieldKey: string
}

type WrapperProps = Omit<Props, 'fieldKey'>

const Wrapper: FC<PropsWithChildren<WrapperProps>> = ({ disableTypography, children, emphasis }) => {
  if (disableTypography) {
    return children
  }

  const Container = emphasis ? H6 : Body1

  return <Container>{children}</Container>
}

export const HighlightedText: FC<PropsWithChildren<Props>> = ({
  children,
  disableTypography,
  emphasis,
  fieldKey,
  matches,
}) => {
  const match = matches?.find((m) => m.key === fieldKey)

  if (!match) {
    return (
      <Wrapper disableTypography={disableTypography} emphasis={emphasis}>
        {children}
      </Wrapper>
    )
  }

  return (
    <Wrapper disableTypography={disableTypography} emphasis={emphasis}>
      {match.highlights.map(([text, highlight], index) => (
        <Box
          component="span"
          // eslint-disable-next-line react/no-array-index-key -- we don't have a unique key available
          key={index}
          sx={{
            backgroundColor: (theme) => (highlight ? theme.palette.secondary.main : 'none'),
            color: (theme) => (highlight ? theme.palette.secondary.contrastText : 'inherit'),
          }}
        >
          {text}
        </Box>
      ))}
    </Wrapper>
  )
}
